/**
 * CustomerActivityPanel — right-side slide-over that opens when a customer
 * name is clicked in the activity feed. Shows a stats strip + chronological
 * timeline for the selected window. Two CTAs at the top:
 *   💬 Message customer  →  deep-links to /dispatch?tab=chat&chat={id}
 *   🔗 View full profile →  /dispatch/admin/users?userId={id}
 *
 * Ship: Feb 2026 — Option B follow-up to the in-app chat feature.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { X, MessageCircle, ExternalLink, Clock, Package, Smartphone, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const WINDOW_OPTIONS = [
  { value: 1, label: '24h' },
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
];

function formatTs(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const diffMin = (now - d) / 60000;
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${Math.floor(diffMin)}m ago`;
  if (diffMin < 60 * 24) return `${Math.floor(diffMin / 60)}h ago`;
  return d.toLocaleString();
}

export default function CustomerActivityPanel({ customerId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/admin/users/${customerId}/activity?days=${days}&limit=200`,
        { withCredentials: true },
      );
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load activity');
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [customerId, days, onClose]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!customerId) return null;

  const user = data?.user;
  const stats = data?.stats || {};
  const events = data?.events || [];

  return (
    <div
      className="fixed inset-0 z-[60] flex justify-end bg-black/60"
      onClick={onClose}
      data-testid="customer-activity-panel"
    >
      <div
        className="w-full sm:w-[520px] sm:max-w-[95vw] bg-[#0C0612] border-l border-[#2A0F3D] flex flex-col h-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — extra top padding to clear the iOS status bar / Dynamic
            Island on mobile PWA. Uses `env(safe-area-inset-top)` when
            available, falls back to a comfortable 20 px otherwise. */}
        <div
          className="px-4 pb-4 border-b border-[#2A0F3D] flex items-start justify-between gap-2"
          style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 20px)' }}
        >
          <div className="min-w-0 flex-1">
            <p className="text-[#39FF14] font-mono text-lg font-bold truncate" data-testid="cap-user-name">
              {user?.name || '(loading…)'}
            </p>
            <p className="text-[#4B6B52] font-mono text-[10px] truncate">
              {user?.email} · {user?.region?.toUpperCase() || '—'} · {user?.role}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-[#A3C7A8] hover:text-[#39FF14] flex-shrink-0"
            aria-label="Close panel"
            data-testid="cap-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* CTAs */}
        <div className="p-3 border-b border-[#2A0F3D] flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => window.location.assign(`/dispatch?tab=chat&chat=${customerId}`)}
            className="flex-1 min-w-[140px] bg-[#B400FF] text-white hover:bg-[#C42BFF] rounded-none font-mono text-xs uppercase tracking-wider py-2 px-3 flex items-center justify-center gap-2"
            data-testid="cap-message-btn"
          >
            <MessageCircle className="w-3.5 h-3.5" />
            MESSAGE CUSTOMER
          </button>
          <button
            type="button"
            onClick={() => window.location.assign(`/dispatch/admin/users?openUser=${customerId}&from=activity-panel`)}
            className="flex-1 min-w-[140px] bg-transparent border border-[#2A0F3D] text-[#39FF14] hover:border-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider py-2 px-3 flex items-center justify-center gap-2"
            data-testid="cap-profile-btn"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            FULL PROFILE
          </button>
        </div>

        {/* Stats strip */}
        <div className="p-3 border-b border-[#2A0F3D] grid grid-cols-4 gap-2">
          <StatCell icon={<Package className="w-3 h-3" />} label="ORDERS" value={stats.orders_count ?? '—'} testid="cap-stat-orders" />
          <StatCell icon={<DollarSign className="w-3 h-3" />} label="SPEND" value={stats.lifetime_spend != null ? `$${stats.lifetime_spend}` : '—'} testid="cap-stat-spend" />
          <StatCell icon={<Smartphone className="w-3 h-3" />} label="DEVICES" value={stats.devices ?? '—'} testid="cap-stat-devices" />
          <StatCell icon={<Clock className="w-3 h-3" />} label="LAST" value={stats.last_active_at ? formatTs(stats.last_active_at) : '—'} testid="cap-stat-last" />
        </div>

        {/* Window picker */}
        <div className="p-3 border-b border-[#2A0F3D] flex items-center gap-2">
          <span className="text-[#4B6B52] font-mono text-[10px] uppercase">WINDOW:</span>
          {WINDOW_OPTIONS.map((w) => (
            <button
              key={w.value}
              type="button"
              onClick={() => setDays(w.value)}
              className={`px-2 py-1 font-mono text-[10px] uppercase border rounded-none ${
                days === w.value
                  ? 'bg-[#39FF14] text-[#050003] border-[#39FF14]'
                  : 'border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14]'
              }`}
              data-testid={`cap-window-${w.value}`}
            >
              {w.label}
            </button>
          ))}
        </div>

        {/* Event timeline */}
        <div className="flex-1 overflow-y-auto p-4" data-testid="cap-events">
          {loading && <p className="text-[#4B6B52] font-mono text-xs text-center py-6">// loading…</p>}
          {!loading && events.length === 0 && (
            <p className="text-[#4B6B52] font-mono text-xs text-center py-6">// no events in this window</p>
          )}
          <ul className="space-y-2">
            {events.map((ev, i) => (
              <li
                key={ev.id || ev._id || i}
                className="border-l-2 border-[#2A0F3D] pl-3 py-1"
                data-testid={`cap-event-${i}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[#39FF14] font-mono text-[10px] uppercase tracking-wider">
                    {ev.event_type || 'event'}
                  </span>
                  <span className="text-[#4B6B52] font-mono text-[10px] flex-shrink-0">
                    {formatTs(ev.ts || ev.created_at)}
                  </span>
                </div>
                {ev.summary && (
                  <p className="text-[#A3C7A8] font-mono text-xs mt-0.5">{ev.summary}</p>
                )}
                {ev.data && Object.keys(ev.data).length > 0 && (
                  <p className="text-[#6B8E73] font-mono text-[10px] mt-0.5 truncate">
                    {JSON.stringify(ev.data).slice(0, 120)}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function StatCell({ icon, label, value, testid }) {
  return (
    <div className="flex flex-col items-start" data-testid={testid}>
      <div className="flex items-center gap-1 text-[#4B6B52] font-mono text-[9px] uppercase">
        {icon}{label}
      </div>
      <p className="text-[#39FF14] font-mono text-sm font-bold truncate w-full">{value}</p>
    </div>
  );
}
